import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ChevronUp, ChevronDown, Copy, User, Clock, ArrowLeft, Image, Trash2, Pencil, Bookmark, MessageCircle, Send, X, Star, Sparkles, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { buildCalcUrl } from '@/utils/promptCostEstimator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromptDetail() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editPlatform, setEditPlatform] = useState('');
  const [editCategories, setEditCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [promptRes, platRes, catRes] = await Promise.all([
          axios.get(`${API}/prompts/${id}`, { headers }),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/categories`)
        ]);
        setPrompt(promptRes.data);
        setPlatforms(platRes.data);
        setCategories(catRes.data);
        const found = platRes.data.find(p => p.id === promptRes.data.platform_id);
        setPlatform(found || null);

        setEditTitle(promptRes.data.title);
        setEditContent(promptRes.data.content);
        setEditPlatform(promptRes.data.platform_id || '');
        setEditCategories(promptRes.data.categories || []);

        const commRes = await axios.get(`${API}/prompts/${promptRes.data.id}/comments`);
        setComments(commRes.data);

        // Fetch random banner
        try {
          const bannerRes = await axios.get(`${API}/banners/random`);
          if (bannerRes.data && bannerRes.data.image) setBanner(bannerRes.data);
        } catch {}
      } catch {
        toast.error('Prompt not found');
        navigate('/');
      }
      setLoading(false);
    };
    fetchAll();
  }, [id, token, navigate]);

  const handleVote = async (direction) => {
    if (!user) { toast.error('Login to vote'); return; }
    try {
      const res = await axios.post(`${API}/prompts/${prompt.id}/upvote?direction=${direction}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPrompt(prev => ({ ...prev, upvotes: res.data.score, user_vote: res.data.vote }));
    } catch { toast.error('Failed to vote'); }
  };

  const handleBookmark = async () => {
    if (!user) { toast.error('Login to bookmark'); return; }
    try {
      const res = await axios.post(`${API}/prompts/${prompt.id}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setPrompt(prev => ({ ...prev, has_bookmarked: res.data.bookmarked }));
      toast.success(res.data.bookmarked ? 'Saved to bookmarks' : 'Removed from bookmarks');
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt.content);
    toast.success('Prompt copied!');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this prompt?')) return;
    try {
      await axios.delete(`${API}/prompts/${prompt.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Prompt deleted');
      navigate('/');
    } catch { toast.error('Failed to delete'); }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editTitle.trim() || !editContent.trim()) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/prompts/${prompt.id}`, {
        title: editTitle, content: editContent,
        categories: editCategories, platform_id: editPlatform,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPrompt(res.data);
      const found = platforms.find(p => p.id === res.data.platform_id);
      setPlatform(found || null);
      setEditOpen(false);
      toast.success('Prompt updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const toggleEditCategory = (cat) => {
    setEditCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Login to comment'); return; }
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      const res = await axios.post(`${API}/prompts/${prompt.id}/comments`, { content: commentText }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [res.data, ...prev]);
      setCommentText('');
      toast.success('Comment posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    }
    setPostingComment(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete comment'); }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-12 animate-pulse">
      <div className="h-8 w-48 bg-muted rounded mb-4" />
      <div className="h-6 w-full bg-muted rounded mb-2" />
      <div className="h-6 w-2/3 bg-muted rounded mb-6" />
      <div className="h-40 bg-muted rounded" />
    </div>
  );

  if (!prompt) return null;

  const canEdit = user?.id === prompt.user_id || user?.role === 'admin';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="prompt-detail-page">
      <Button variant="ghost" className="mb-6 gap-2 -ml-2 text-muted-foreground" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      {/* Ad Banner */}
      {banner && banner.image && (
        <div className="mb-4 flex justify-center" data-testid="ad-banner">
          {banner.click_url ? (
            <a
              href={banner.click_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
              onClick={() => axios.post(`${API}/banners/${banner.id}/click`).catch(() => {})}
            >
              <img src={banner.image} alt={banner.name || 'Ad'} style={{ width: 540, height: 'auto' }} className="rounded-lg" />
            </a>
          ) : (
            <img src={banner.image} alt={banner.name || 'Ad'} style={{ width: 540, height: 'auto' }} className="rounded-lg" />
          )}
        </div>
      )}

      <Card className="p-6 sm:p-8 border-border/50">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {prompt.categories?.map(cat => (
            <Link key={cat} to={`/?category=${cat}`}>
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 rounded-full">{cat}</Badge>
            </Link>
          ))}
          {platform && (
            <Badge variant="outline" className="rounded-full gap-1.5">{platform.name}</Badge>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="prompt-detail-title">{prompt.title}</h1>

        {/* Author & Date */}
        <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
          <Link to={`/profile/${prompt.user_id}`} className="flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="prompt-author-link">
            <User className="w-3.5 h-3.5" /> {prompt.username}
          </Link>
          {prompt.author_karma != null && (
            <span className="flex items-center gap-1 text-xs font-medium text-amber-500" data-testid="author-karma-badge">
              <Star className="w-3 h-3 fill-amber-500" /> {prompt.author_karma} karma
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {new Date(prompt.created_at).toLocaleDateString()}
          </span>
          {prompt.updated_at && (
            <span className="text-xs italic">(edited)</span>
          )}
        </div>

        {/* Content */}
        <div className="bg-muted/50 rounded-lg p-5 mb-6 border border-border/50" data-testid="prompt-content">
          <pre className="whitespace-pre-wrap text-sm leading-relaxed font-mono">{prompt.content}</pre>
        </div>

        {/* Screenshots */}
        {prompt.screenshots?.length > 0 && (
          <div className="mb-6" data-testid="prompt-screenshots">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Image className="w-4 h-4" /> Screenshots ({prompt.screenshots.length})
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {prompt.screenshots.map((src, i) => (
                <Dialog key={i}>
                  <DialogTrigger asChild>
                    <button className="rounded-lg overflow-hidden border border-border hover:border-primary/50 transition-colors aspect-video bg-muted" data-testid={`screenshot-${i}`}>
                      <img src={src} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl p-2">
                    <img src={src} alt={`Screenshot ${i + 1}`} className="w-full rounded-lg" />
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-border/50">
          <div className="flex items-center gap-1 rounded-full border border-border px-1 py-0.5">
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 rounded-full ${prompt.user_vote === 'up' ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary'}`}
              onClick={() => handleVote('up')}
              data-testid="detail-upvote-btn"
            >
              <ChevronUp className="w-5 h-5" strokeWidth={2.5} />
            </Button>
            <span className={`text-sm font-bold tabular-nums min-w-[2ch] text-center ${(prompt.upvotes || 0) > 0 ? 'text-primary' : (prompt.upvotes || 0) < 0 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="detail-score">
              {prompt.upvotes || 0}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 rounded-full ${prompt.user_vote === 'down' ? 'text-destructive bg-destructive/10' : 'text-muted-foreground hover:text-destructive'}`}
              onClick={() => handleVote('down')}
              data-testid="detail-downvote-btn"
            >
              <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
            </Button>
          </div>
          <Button
            variant={prompt.has_bookmarked ? 'default' : 'outline'}
            className={`rounded-full gap-2 ${prompt.has_bookmarked ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20' : ''}`}
            onClick={handleBookmark}
            data-testid="detail-bookmark-btn"
          >
            <Bookmark className={`w-4 h-4 ${prompt.has_bookmarked ? 'fill-current' : ''}`} />
            {prompt.has_bookmarked ? 'Saved' : 'Save'}
          </Button>
          <Button variant="outline" className="rounded-full gap-2" onClick={handleCopy} data-testid="detail-copy-btn">
            <Copy className="w-4 h-4" /> Copy
          </Button>
          <Link to={buildCalcUrl(prompt, platform?.name)}>
            <Button variant="outline" className="rounded-full gap-2 text-primary border-primary/30 hover:bg-primary/10" data-testid="detail-calc-cost-btn">
              <DollarSign className="w-4 h-4" /> Estimate Cost
            </Button>
          </Link>
          {canEdit && (
            <>
              <Button variant="outline" className="rounded-full gap-2 ml-auto" onClick={() => setEditOpen(true)} data-testid="detail-edit-btn">
                <Pencil className="w-4 h-4" /> Edit
              </Button>
              <Button variant="outline" className="rounded-full gap-2 text-destructive hover:bg-destructive/10" onClick={handleDelete} data-testid="detail-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Shoutout Section */}
      {prompt.shoutout && (
        <Card className="mt-4 p-4 border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20" data-testid="shoutout-section">
          <Link to={`/prompt/${prompt.shoutout.slug || prompt.shoutout.id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
            <div>
              <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Shoutout to</span>
              <p className="text-sm font-semibold">{prompt.shoutout.title}</p>
              <p className="text-xs text-muted-foreground">by {prompt.shoutout.username}</p>
            </div>
          </Link>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Prompt</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-5 mt-2" data-testid="edit-prompt-form">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="h-10" data-testid="edit-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Prompt Content</Label>
              <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} required className="min-h-[130px] font-mono text-sm" data-testid="edit-content-input" />
            </div>
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={editPlatform} onValueChange={setEditPlatform}>
                <SelectTrigger className="h-10" data-testid="edit-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Categories</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="edit-category-selector">
                {categories.map(cat => (
                  <Badge
                    key={cat.id}
                    variant={editCategories.includes(cat.name) ? 'default' : 'outline'}
                    className={`cursor-pointer rounded-full text-xs transition-all ${editCategories.includes(cat.name) ? 'shadow-sm' : 'hover:bg-accent'}`}
                    onClick={() => toggleEditCategory(cat.name)}
                  >
                    {cat.name}
                    {editCategories.includes(cat.name) && <X className="w-2.5 h-2.5 ml-1" />}
                  </Badge>
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full h-10 rounded-full shadow-lg shadow-primary/20 gap-2" disabled={saving} data-testid="edit-save-btn">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Comments Section */}
      <Card className="mt-6 p-6 sm:p-8 border-border/50" data-testid="comments-section">
        <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" /> Discussion ({comments.length})
        </h2>

        {/* Comment input */}
        <form onSubmit={handlePostComment} className="flex gap-2 mb-6" data-testid="comment-form">
          <div className="relative flex-1">
            <Textarea
              placeholder={user ? "Add a comment..." : "Login to comment..."}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              disabled={!user}
              className="min-h-[60px] resize-y"
              rows={2}
              data-testid="comment-input"
            />
          </div>
          <Button type="submit" size="sm" disabled={!user || postingComment || !commentText.trim()} className="rounded-full h-10 px-4 gap-1.5 self-end" data-testid="comment-submit-btn">
            <Send className="w-3.5 h-3.5" /> Post
          </Button>
        </form>

        {/* Comment list */}
        {comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-comments">No comments yet. Be the first to share your thoughts!</p>
        ) : (
          <div className="space-y-4" data-testid="comments-list">
            {comments.map(comment => (
              <div key={comment.id} className="flex gap-3 group" data-testid={`comment-${comment.id}`}>
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  {comment.username?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/profile/${comment.user_id}`} className="text-sm font-semibold hover:text-primary transition-colors">
                      {comment.username}
                    </Link>
                    {comment.author_karma != null && (
                      <span className="flex items-center gap-0.5 text-[10px] font-medium text-amber-500" data-testid={`comment-karma-${comment.id}`}>
                        <Star className="w-2.5 h-2.5 fill-amber-500" /> {comment.author_karma}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{new Date(comment.created_at).toLocaleDateString()}</span>
                    {(user?.id === comment.user_id || user?.role === 'admin') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteComment(comment.id)}
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
